"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { motion } from "framer-motion"
import {
  Eye, Brain, Puzzle, HeartHandshake, HeartPulse,
  Scale, Users, Crown, ChevronLeft, ChevronRight,
} from "lucide-react"

const services = [
  {
    icon: Eye,
    title: "EMDR Therapy",
    href: "/emdr-therapy",
    description:
      "For those carrying the lasting effects of trauma, EMDR helps the brain process distressing experiences so they feel less overwhelming over time. It can reduce the intensity of painful memories and help you move forward with greater clarity and stability.",
    tag: "Evidence-Based",
    image: "/images/services/emdr-therapy-session.jpg",
    imageAlt: "Client in a focused EMDR therapy session",
  },
  {
    icon: Brain,
    title: "EMDR Intensive",
    href: "/emdr-intensive",
    description:
      "Condense weeks of EMDR therapy into two immersive days with 12 in-person hours, offered for both weekday and weekend schedules. This intensive combines skilled trauma therapy with the gentle support of Askari and Obi from our Pet Therapy team.",
    tag: "Self-Pay",
    image: "/images/services/emdr-intensive-session.jpg",
    imageAlt: "EMDR intensive therapy session with bilateral stimulation technique",
  },
  {
    icon: Puzzle,
    title: "ADHD and Autism",
    href: "/adhd-autism-assessments",
    description:
      "Our assessments help children and individuals gain clarity around ADHD or autism through interviews, questionnaires, observations, and evidence-based diagnostic tools. With options for both screening and full assessments, the process provides clearer answers, practical recommendations, and personalized guidance.",
    tag: "Self-Pay",
    image: "/images/services/adhd-autism-571630.jpg",
    imageAlt: "ADHD and autism assessment consultation with a licensed clinician",
  },
  {
    icon: HeartHandshake,
    title: "Trauma Therapy",
    href: "/trauma-therapy",
    description:
      "Our trauma therapists in Greensboro help you work through painful experiences, reframe distressing reactions, and build healthier patterns of thinking and coping. Each session supports clients who have faced trauma such as abuse, loss, domestic violence, or other life-altering events.",
    tag: "Insurance Accepted",
    image: "/images/services/trauma-therapy-session.jpg",
    imageAlt: "Teen in a trauma therapy session with a licensed counselor",
  },
  {
    icon: HeartPulse,
    title: "Individual Therapy",
    href: "/individual-therapy",
    description:
      "Through one-on-one support, you can explore depression, anxiety, grief, life transitions, hopelessness, or thoughts of suicide in a safe, compassionate space. Sessions may also include animal-assisted therapy with Obi and Askari, helping you build healthier perspectives and navigate life with greater support and stability.",
    tag: "Insurance Accepted",
    image: "/images/services/individual-therapy-session.jpg",
    imageAlt: "One-on-one individual therapy session with a licensed counselor",
  },
  {
    icon: Scale,
    title: "DWI and Substance Abuse Therapy",
    href: "/substance-abuse-therapy",
    description:
      "The program provides comprehensive assessment and treatment for individuals with a DWI offense, combining legal compliance with support for long-term well-being. It includes individualized assessments, substance abuse counseling, skill-building education, relapse prevention, and access to community resources.",
    tag: "Self-Pay",
    image: "/images/services/dwi-substance-session.jpg",
    imageAlt: "African American man in a one-on-one substance abuse counseling session",
  },
  {
    icon: Users,
    title: "Couples Relationship Intensive Therapy",
    href: "/couples-intensive-therapy",
    description:
      "This focused therapy format gives your relationship dedicated time and support to work through conflict, disconnection, affair recovery, or major decisions. With extended, structured sessions, it creates space to address recurring patterns, strengthen communication, and engage in the repair work your relationship may need.",
    tag: "Self-Pay",
    image: "/images/services/couples-conflict-session.jpg",
    imageAlt: "Couples working through conflict in an intensive therapy session",
  },
  {
    icon: Crown,
    title: "Concierge Cash Membership Programs",
    href: "/concierge-membership-program",
    description:
      "Our membership programs offer an elevated support model for those who benefit from more regular therapeutic contact and added continuity between sessions. Available for individuals and couples, these options are designed to make care feel more integrated, accessible, and responsive over time.",
    tag: "Self-Pay",
    image: "/images/services/concierge-office-session.jpg",
    imageAlt: "Modern bright therapy office for concierge membership clients",
  },
]

const tagColors: Record<string, string> = {
  "Evidence-Based":     "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-dark))]",
  "Insurance Accepted": "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-dark))]",
  "Self-Pay":           "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-dark))]",
}

const GAP = 24 // px — matches gap-6

const ServicesSection = () => {
  const [page, setPage]           = useState(0)
  const [cardsPerPage, setCpp]    = useState(3)
  const [cardWidth, setCardWidth] = useState(0)
  const trackRef = useRef<HTMLDivElement>(null)

  const totalPages = Math.ceil(services.length / cardsPerPage)

  const measure = useCallback(() => {
    const w = window.innerWidth
    const cpp = w >= 1024 ? 3 : w >= 640 ? 2 : 1
    setCpp(cpp)
    setPage(0)
    if (trackRef.current) {
      const cw = (trackRef.current.offsetWidth - (cpp - 1) * GAP) / cpp
      setCardWidth(cw)
    }
  }, [])

  useEffect(() => {
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [measure])

  // pixels to translate per page
  const slideX = -(page * cardsPerPage * (cardWidth + GAP))

  const prev = () => setPage((p) => Math.max(p - 1, 0))
  const next = () => setPage((p) => Math.min(p + 1, totalPages - 1))

  return (
    <section id="services" className="section-padding bg-[hsl(var(--sand))]/40">
      <div className="max-w-6xl mx-auto">

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <p className="text-sm font-medium tracking-[0.18em] uppercase text-[hsl(var(--terracotta))] mb-3">
            What We Offer
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mb-4 leading-snug">
            Specialized care tailored to where you are<br className="hidden sm:block" /> and what you need most
          </h2>
          <div className="w-14 h-0.5 bg-[hsl(var(--sage))] mx-auto mb-6" />
          <p className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Life can feel overwhelming, but healing is possible with Battlefield Counseling in Greensboro, NC.
            We serve children, teens, adults, and couples seeking support for trauma, anxiety, relationship
            challenges, addiction, and other mental health concerns. Whatever you are facing, we have the
            tools, the training, and the dedication to walk this road with you.
          </p>
        </motion.div>

        {/* Slider track */}
        <div ref={trackRef} className="overflow-hidden mt-12">
          <motion.div
            className="flex"
            style={{ gap: `${GAP}px` }}
            animate={{ x: slideX }}
            transition={{ type: "spring", damping: 32, stiffness: 260, mass: 0.9 }}
          >
            {services.map((service, i) => {
              const Icon = service.icon
              return (
                <div
                  key={i}
                  className="flex-none flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm border border-[hsl(var(--border))] hover:shadow-md transition-shadow group"
                  style={{
                    width: cardWidth > 0
                      ? `${cardWidth}px`
                      : `calc(${100 / cardsPerPage}% - ${(GAP * (cardsPerPage - 1)) / cardsPerPage}px)`,
                  }}
                >
                  {/* Image */}
                  <div className="relative h-44 overflow-hidden bg-[hsl(var(--sage-light))]/40">
                    <img
                      src={service.image}
                      alt={service.imageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                      width={400}
                      height={176}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <span
                      className={`absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full ${
                        tagColors[service.tag] ?? "bg-white/80 text-foreground"
                      }`}
                    >
                      {service.tag}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-xl bg-[hsl(var(--sage-light))] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-[hsl(var(--sage-dark))]" />
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground leading-snug">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                      {service.description}
                    </p>
                    <a
                      href={service.href}
                      className="inline-flex items-center mt-5 text-sm font-semibold text-[hsl(var(--terracotta))] hover:underline underline-offset-2 transition-colors"
                    >
                      LEARN MORE →
                    </a>
                  </div>
                </div>
              )
            })}
          </motion.div>
        </div>

        {/* Navigation — arrows + dots */}
        <div className="flex justify-center items-center gap-5 mt-8">
          <button
            onClick={prev}
            disabled={page === 0}
            aria-label="Previous services"
            className="w-10 h-10 rounded-full border border-[hsl(var(--sage))]/40 bg-white flex items-center justify-center hover:border-[hsl(var(--sage-dark))] hover:bg-[hsl(var(--sage-light))]/40 transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
          >
            <ChevronLeft className="w-5 h-5 text-[hsl(var(--sage-dark))]" />
          </button>

          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                aria-label={`Go to page ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === page
                    ? "bg-[hsl(var(--terracotta))] w-6"
                    : "bg-[hsl(var(--sage))]/35 w-2 hover:bg-[hsl(var(--sage))]/60"
                }`}
              />
            ))}
          </div>

          <button
            onClick={next}
            disabled={page === totalPages - 1}
            aria-label="Next services"
            className="w-10 h-10 rounded-full border border-[hsl(var(--sage))]/40 bg-white flex items-center justify-center hover:border-[hsl(var(--sage-dark))] hover:bg-[hsl(var(--sage-light))]/40 transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
          >
            <ChevronRight className="w-5 h-5 text-[hsl(var(--sage-dark))]" />
          </button>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.55 }}
          className="text-center mt-14"
        >
          <p className="text-muted-foreground text-sm mb-5">
            Not sure which service is right for you? We&apos;ll help you find the best fit.
          </p>
          <a
            href="/book-a-session"
            className="inline-flex items-center justify-center rounded-full bg-[hsl(var(--terracotta))] text-white px-10 py-4 text-base font-semibold tracking-wide hover:bg-[hsl(var(--terracotta))]/90 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
          >
            BOOK AN APPOINTMENT
          </a>
        </motion.div>

      </div>
    </section>
  )
}

export default ServicesSection
